import Api from "../../../../../../../../core/services/api/api";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {getCurrentSerializableFromKeyInfo, parseImportIsSerialized,} from "./shared/serializability-boolean";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

const API_CALL_SPACING_MS = 100;

/** When true, skips per-row `getProductKeyInformation` and always emits `partId` for valid rows. */
const SkipKeyInformationLookup = true;

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

/**
 * Part serializability import — phased validation (see {@link BaseImportTransformer} run contract).
 * 1) Missing part number (CSV)
 * 2) Batched part id lookup (cached via context; HTTP skipped when there are no part numbers)
 * 3) Unknown part
 * 4) `isSerialized` boolean parse (same rule as legacy: invalid only when the cell has a value that does not parse)
 * 5) Per-row `getProductKeyInformation` when {@link SkipKeyInformationLookup} is false — feeds payload pass only
 *
 * Preview (`!isFinalRun`): placeholders where needed + merged `preventativeIssues`.
 * Final (`isFinalRun`): `null` for rows with issues; otherwise `{ partId, isSerialized }` or `null` when no API update is needed.
 */
export class PartSerializabilityImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            codes: [],
        }));

        // ── Phase 1: preliminary (CSV-only) ─────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 2: batched part lookup ────────────────────────────────────────
        const uniqueParts = [...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        // ── Phase 3: unknown part ───────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 4: serialized boolean (not gated on `partId`; stacks with INVALID_PART) ──
        for (const rs of rowState) {
            if (!rs.item) continue;
            rs.targetIsSerialized = parseImportIsSerialized(rs.item.isSerialized);
            if (rs.targetIsSerialized === undefined) {
                rs.codes.push(PreventionInvalidityCode.INVALID_BOOLEAN);
            }
        }

        // ── Phase 5: key information (skipped when SkipKeyInformationLookup) ─────
        /** @type {(null|{ targetIsSerialized: boolean, response: * })[]} */
        const keyInfoSlotByRowIndex = new Array(rows.length).fill(null);
        if (!SkipKeyInformationLookup) {
            await Promise.all(
                rowState.map(async (rs, index) => {
                    if (!rs.item || !rs.partNoUpper) return;
                    const partId = partIds[rs.partNoUpper];
                    if (!partId) return;
                    if (rs.targetIsSerialized === undefined) return;

                    await wait(index * API_CALL_SPACING_MS);

                    const keyInfoResponse = await Api.getProductKeyInformation(rs.partNoUpper);
                    keyInfoSlotByRowIndex[index] = {targetIsSerialized: rs.targetIsSerialized, response: keyInfoResponse};
                }),
            );
        }

        // ── Emit merged issues ───────────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {partId: null, partNumber: undefined};
            }

            const rs = rowState[index];
            if (!rs.partNoUpper) {
                return isFinalRun ? null : {partId: null, partNumber: item.partNumber};
            }

            const partId = partIds[rs.partNoUpper];
            const hasIssue = issueIndexSet.has(index);

            if (hasIssue) {
                return isFinalRun ? null : {partId: partId ?? null, partNumber: item.partNumber};
            }

            const buildSerializabilityRequest = () => (
                isFinalRun
                    ? {partId, isSerialized: rs.targetIsSerialized}
                    : {
                        partId: partId ?? null,
                        partNumber: item.partNumber,
                        isSerialized: rs.targetIsSerialized,
                    }
            );

            if (SkipKeyInformationLookup) {
                return buildSerializabilityRequest();
            }

            const slot = keyInfoSlotByRowIndex[index];
            if (!slot?.response?.resultFlag) {
                return null;
            }

            const currentIsSerializable = getCurrentSerializableFromKeyInfo(slot.response?.data);
            if (currentIsSerializable === rs.targetIsSerialized) {
                return null;
            }

            return buildSerializabilityRequest();
        });

        return {apiRequests, preventativeIssues};
    }
}
